import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/apiConstants';
import { getStoredTokens, getUserInfo } from '../utils/auth';
import Navbar from '../components/Navbar';
import './CSS/Chatbot.css';

const CHAT_PRIMER = "Adflyn AI — your marketing automation agent. Ask me to schedule posts, cancel or reschedule them, get strategy advice, or anything else. I'll handle it.";

const LinkedInPreviewCard = ({ preview, onAction }) => {
  if (!preview) return null;
  const tags = Array.isArray(preview.hashtags) ? preview.hashtags.join(' ') : (preview.hashtags || '');

  const card = {
    background: '#ffffff',
    border: '1px solid #dde1e7',
    borderRadius: '12px',
    padding: '16px',
    maxWidth: '460px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
    marginTop: '10px',
    color: '#111111',
  };
  const header = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' };
  const logo = { width: '42px', height: '42px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' };
  const logoFallback = {
    width: '42px', height: '42px', borderRadius: '8px', background: '#0a66c2',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: '700', fontSize: '16px', flexShrink: 0,
  };
  const orgName = { fontWeight: '700', fontSize: '14px', color: '#111111', display: 'block' };
  const scheduled = { fontSize: '11px', color: '#666666', display: 'block', marginTop: '2px' };
  const headline = { fontWeight: '700', fontSize: '15px', color: '#111111', margin: '0 0 8px 0', lineHeight: '1.4' };
  const body = { fontSize: '13px', color: '#333333', whiteSpace: 'pre-line', margin: '0 0 8px 0', lineHeight: '1.6' };
  const cta = { fontSize: '13px', color: '#111111', fontWeight: '500', margin: '0 0 8px 0' };
  const hashtags = { fontSize: '12px', color: '#0a66c2', margin: '0 0 10px 0' };
  const badge = {
    display: 'inline-block', fontSize: '10px', background: '#f0f4ff', color: '#0a66c2',
    border: '1px solid #c8d8ff', borderRadius: '20px', padding: '2px 10px',
    marginBottom: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px',
  };
  const divider = { borderTop: '1px solid #eee', margin: '12px 0' };
  const actionsRow = { display: 'flex', flexWrap: 'wrap', gap: '8px' };

  const btnBase = {
    border: 'none', borderRadius: '20px', padding: '8px 16px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer', lineHeight: '1',
  };
  const btnApprove = { ...btnBase, background: '#0a66c2', color: '#ffffff' };
  const btnSecondary = { ...btnBase, background: '#f3f2ef', color: '#333333', border: '1px solid #ddd' };

  return (
    <div style={card}>
      {/* Header */}
      <div style={header}>
        {preview.logo_url
          ? <img src={preview.logo_url} alt={preview.org_name} style={logo} />
          : <div style={logoFallback}>{(preview.org_name || 'L')[0].toUpperCase()}</div>
        }
        <div>
          <span style={orgName}>{preview.org_name}</span>
          {preview.scheduled_at_display && (
            <span style={scheduled}>LinkedIn · {preview.scheduled_at_display}</span>
          )}
        </div>
      </div>

      {/* Content */}
      {preview.headline && <p style={headline}>{preview.headline}</p>}
      {preview.body && <p style={body}>{preview.body}</p>}
      {preview.cta && <p style={cta}>{preview.cta}</p>}
      {tags && <p style={hashtags}>{tags}</p>}
      {preview.template && <span style={badge}>{preview.template}</span>}

      {/* Image hint */}
      {preview.image_prompt && (
        <div style={{ fontSize: '11px', color: '#888', background: '#fafafa', borderRadius: '6px', padding: '6px 10px', marginBottom: '12px', border: '1px solid #eee' }}>
          🖼️ <em>{preview.image_prompt}</em>
        </div>
      )}

      <div style={divider} />

      {/* Action buttons */}
      <div style={actionsRow}>
        <button style={btnApprove} onClick={() => onAction('approve')}>✓ Approve &amp; Schedule</button>
        <button style={btnSecondary} onClick={() => onAction('edit')}>✏️ Edit Content</button>
        <button style={btnSecondary} onClick={() => onAction('change_org')}>🏢 Change Org</button>
        <button style={btnSecondary} onClick={() => onAction('change_time')}>🕐 Different Time</button>
      </div>
    </div>
  );
};

LinkedInPreviewCard.propTypes = {
  preview: PropTypes.object.isRequired,
};

const ConnectAccountCard = ({ platform, message }) => {
  const navigate = useNavigate();
  const config = {
    linkedin:  { label: 'Connect LinkedIn',  bg: '#0a66c2' },
    facebook:  { label: 'Connect Facebook',  bg: '#1877f2' },
    instagram: { label: 'Connect Instagram', bg: '#e1306c' },
  }[platform] || { label: 'Connect Account', bg: '#0a66c2' };

  return (
    <div style={{
      background: '#fff', border: '1px solid #dde1e7', borderRadius: '12px',
      padding: '16px 20px', maxWidth: '380px', fontFamily: 'inherit'
    }}>
      <p style={{ fontSize: '14px', color: '#333', margin: '0 0 14px 0', lineHeight: '1.5' }}>
        {message}
      </p>
      <button
        style={{
          background: config.bg, color: '#fff', border: 'none',
          borderRadius: '20px', padding: '8px 20px', fontSize: '13px',
          fontWeight: '600', cursor: 'pointer'
        }}
        onClick={() => navigate('/social-media-automation')}
      >
        {config.label}
      </button>
    </div>
  );
};

ConnectAccountCard.propTypes = {
  platform: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
};

const LinkedInCampaignCard = ({ message, onSchedule }) => {
  const [showAll, setShowAll] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduledCount, setScheduledCount] = useState(null);

  const plan = message.data || {};
  const execution = message.execution;
  const posts = plan.posts || [];
  const visiblePosts = showAll ? posts : posts.slice(0, 5);

  const handleSchedule = async () => {
    setScheduling(true);
    const result = await onSchedule(message.userMessage, true);
    setScheduling(false);
    const count = result?.execution?.scheduled ?? result?.plan?.posts?.length ?? 0;
    setScheduledCount(count);
  };

  let doneCount = null;
  if (scheduledCount !== null) {
    doneCount = scheduledCount;
  } else if (execution) {
    doneCount = execution.scheduled;
  }

  return (
    <div className="linkedin-campaign-card">
      <h3 className="lc-header">LinkedIn Campaign Plan</h3>
      {plan.date_range && (
        <p className="lc-date-range">
          {plan.date_range.start} &rarr; {plan.date_range.end}
        </p>
      )}
      {plan.posting_frequency && (
        <span className="lc-frequency-tag">{plan.posting_frequency}</span>
      )}
      {plan.campaign_summary && <p className="lc-summary">{plan.campaign_summary}</p>}
      {plan.themes && plan.themes.length > 0 && (
        <div className="lc-themes">
          {plan.themes.map((theme) => (
            <span key={theme} className="lc-theme-pill">{theme}</span>
          ))}
        </div>
      )}
      <div className="lc-posts">
        {visiblePosts.map((post) => (
          <div key={post.suggested_date} className="lc-post-card">
            <div className="lc-post-meta">Week {post.week} &bull; {post.suggested_date}</div>
            <div className="lc-post-topic">{post.topic}</div>
            <div className="lc-post-caption">
              {post.caption && post.caption.length > 150
                ? post.caption.substring(0, 150) + '…'
                : post.caption}
            </div>
            {post.hashtags && post.hashtags.length > 0 && (
              <div className="lc-post-hashtags">{post.hashtags.join(' ')}</div>
            )}
          </div>
        ))}
      </div>
      {posts.length > 5 && (
        <button className="lc-toggle-btn" onClick={() => setShowAll(v => !v)}>
          {showAll ? 'Show fewer posts' : `Show all ${posts.length} posts`}
        </button>
      )}
      {doneCount === null ? (
        <button
          className="lc-schedule-btn"
          onClick={handleSchedule}
          disabled={scheduling}
        >
          {scheduling ? (
            <span className="lc-spinner">Scheduling…</span>
          ) : (
            'Schedule All Posts'
          )}
        </button>
      ) : (
        <div className="lc-scheduled-badge">{doneCount} posts scheduled</div>
      )}
    </div>
  );
};

LinkedInCampaignCard.propTypes = {
  message: PropTypes.shape({
    data: PropTypes.object,
    execution: PropTypes.shape({ scheduled: PropTypes.number }),
    userMessage: PropTypes.string,
  }).isRequired,
  onSchedule: PropTypes.func.isRequired,
};

const ChatBot = () => {
  const navigate = useNavigate();
  // Initialize or retrieve persistent session ID
  const [sessionId] = useState(() => {
    const storedSessionId = localStorage.getItem('chatbot_session_id');
    if (storedSessionId) {
      console.log('Restored session ID:', storedSessionId);
      return storedSessionId;
    }
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem('chatbot_session_id', newSessionId);
    console.log('Created new session ID:', newSessionId);
    return newSessionId;
  });

  const [messages, setMessages] = useState([
    {
      id: 1,
      text: CHAT_PRIMER,
      sender: 'ai',
      type: 'text'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [linkedInConversationActive, setLinkedInConversationActive] = useState(false);
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const hasLoadedHistory = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history on component mount
  useEffect(() => {
    const loadChatHistory = async () => {
      if (hasLoadedHistory.current) return;
      hasLoadedHistory.current = true;

      setIsLoadingHistory(true);
      try {
        const tokens = getStoredTokens();
        if (!tokens || !tokens.idToken) {
          console.log('No authentication tokens, skipping history load');
          setIsLoadingHistory(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.CHATBOT_HISTORY_LOAD}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${tokens.idToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          console.warn('Failed to load chat history:', response.status);
          setIsLoadingHistory(false);
          return;
        }

        const data = await response.json();
        if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
          // Restore campaign card data that was persisted alongside the message
          const restored = data.messages.map((m) =>
            m.type === 'linkedin_campaign'
              ? { ...m, data: m.campaignData || m.data || {}, userMessage: m.userMessage || '' }
              : m
          );
          setMessages(restored);
          console.log(`✅ Loaded ${data.messages.length} messages from history`);
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadChatHistory();
  }, []);

  /**
   * Save a message to the database
   */
  const saveMessageToDatabase = async (message) => {
    try {
      const tokens = getStoredTokens();
      if (!tokens || !tokens.idToken) return;

      await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHATBOT_HISTORY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}`
        },
        body: JSON.stringify({
          sessionId: sessionId,
          message: {
            id: message.id,
            text: message.text,
            sender: message.sender,
            type: message.type,
            imageUrl: message.imageUrl,
            imageIndex: message.imageIndex,
            totalImages: message.totalImages,
            intent: message.intent,
            confidence: message.confidence,
            campaignData: message.type === 'linkedin_campaign' ? (message.data || null) : undefined,
            userMessage: message.userMessage || undefined,
          }
        })
      });
    } catch (error) {
      console.error('Error saving message to database:', error);
    }
  };

  /**
   * Log an action (poster generation, approval, automation, etc.)
   */
  const logAction = async (actionType, actionDetails = {}, status = 'completed', result = null, errorMessage = null) => {
    try {
      const tokens = getStoredTokens();
      if (!tokens || !tokens.idToken) return;

      await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHATBOT_ACTION_LOG}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}`
        },
        body: JSON.stringify({
          sessionId: sessionId,
          actionType: actionType,
          actionDetails: actionDetails,
          status: status,
          result: result,
          errorMessage: errorMessage
        })
      });

      console.log(`[Action Log] ${actionType}:`, actionDetails);
    } catch (error) {
      console.error('Error logging action:', error);
    }
  };


  const triggerLinkedInCampaign = async (userMessage, execute = false) => {
    setIsLoading(true);
    try {
      const tokens = getStoredTokens();
      if (!tokens?.idToken) throw new Error('Please log in to use the chatbot');
      const userInfo = getUserInfo();
      const userId = tokens.userId || userInfo?.id || '';
      const userEmail = userInfo?.email || '';
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHATBOT_LINKEDIN_CAMPAIGN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}`,
        },
        body: JSON.stringify({ userId, userEmail, sessionId, userMessage, execute, userTimezone }),
      });
      // Check ok BEFORE parsing JSON to avoid 'Unexpected token <' on HTML error pages
      if (!response.ok) {
        const ct = response.headers.get('content-type') || '';
        let errMsg = 'Failed to generate campaign plan';
        if (ct.includes('application/json')) {
          const errData = await response.json().catch(() => ({}));
          errMsg = errData?.error?.message || errData?.message || errMsg;
        }
        throw new Error(errMsg);
      }
      const data = await response.json();

      if (data.scheduled === true) {
        // Post was created (SCHEDULE_APPROVED flow or AI tool scheduled it)
        const confirmedMsg = {
          id: Date.now() + 1,
          sender: 'ai',
          type: 'text',
          text: data.agent_reply || 'Your LinkedIn post has been scheduled. ✓',
        };
        setMessages(prev => [...prev, confirmedMsg]);
        await saveMessageToDatabase(confirmedMsg);
        setLinkedInConversationActive(false);
      } else if (data.plan && data.plan.posts && data.plan.posts.length > 0) {
        // Bulk campaign — show the card as before
        const campaignMsg = {
          id: Date.now() + 1,
          sender: 'ai',
          type: 'linkedin_campaign',
          text: '',
          data: data.plan,
          execution: data.execution,
          userMessage,
        };
        setMessages(prev => [...prev, campaignMsg]);
        await saveMessageToDatabase(campaignMsg);
      } else {
        // Agent handled it — check for a preview block first
        const agentText = data.agent_reply || data.plan?.campaign_summary || 'Done.';

        // Check for CONNECT_REQUIRED signal
        const connectMatch = agentText.match(/^CONNECT_REQUIRED:\s*(\{[\s\S]*?\})/);
        if (connectMatch) {
          try {
            const connectData = JSON.parse(connectMatch[1]);
            const connectMsg = {
              id: Date.now() + 1,
              sender: 'ai',
              type: 'connect_required',
              platform: connectData.platform,
              text: connectData.message,
            };
            setMessages(prev => [...prev, connectMsg]);
            await saveMessageToDatabase(connectMsg);
            return;
          } catch { /* fall through to normal text */ }
        }

        const extractPreview = (text) => {
          const match = text.match(/```preview\s*([\s\S]*?)```/);
          if (!match) return null;
          try { return JSON.parse(match[1].trim()); } catch { return null; }
        };
        const parsedPreview = extractPreview(agentText);
        const replyMsg = parsedPreview
          ? {
              id: Date.now() + 1,
              sender: 'ai',
              type: 'linkedin_preview',
              text: agentText,
              preview: parsedPreview,
            }
          : {
              id: Date.now() + 1,
              sender: 'ai',
              type: 'text',
              text: agentText,
            };
        setMessages(prev => [...prev, replyMsg]);
        await saveMessageToDatabase(replyMsg);
        // Stay in LinkedIn mode if agent asked a follow-up; exit if preview was shown
        if (parsedPreview) {
          setLinkedInConversationActive(false);
        } else {
          setLinkedInConversationActive(true);
        }
      }
      window.dispatchEvent(new CustomEvent('adflyn:posts:updated'));
      return data;
    } catch (error) {
      const errorMsg = {
        id: Date.now() + 1,
        text: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        sender: 'ai',
        type: 'text',
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const prompt = inputValue.trim();

    if (!prompt) return;

    // Add user message
    const newMessage = {
      id: Date.now(),
      text: prompt,
      sender: 'user',
      type: 'text',
      files: null
    };

    setMessages(prev => [...prev, newMessage]);
    await saveMessageToDatabase(newMessage);

    // All messages go through the unified agent — it uses LangChain tools
    // to decide whether to answer conversationally, schedule, cancel, reschedule, etc.
    setLinkedInConversationActive(false);
    setInputValue('');
    await triggerLinkedInCampaign(prompt, false);
  };

  return (
    <>
      <Navbar />
      <div className="chat-container">
        <div className="chat-hero">
          <div className="hero-copy">
            <p className="eyebrow">Adflyn AI</p>
            <h1>Text-first strategy desk</h1>
            <p className="lead">Concise, research-backed answers with confidence labels. For visuals and posters, jump into the Gallery.</p>
            <div className="hero-actions">
              <button className="primary" type="button" onClick={() => navigate('/gallery')}>Open Gallery</button>
              <button className="ghost" type="button" onClick={() => navigate('/social-media-automation')}>Social automation</button>
            </div>
            <div className="hero-guidelines">
              <span>Text-only chat</span>
              <span>Budget-safe advice</span>
              <span>Tables for comparisons</span>
            </div>
          </div>
          <div className="hero-card">
            <h3>Guardrails</h3>
            <ul>
              <li>Blocks illegal or deceptive requests</li>
              <li>Flags budget waste & confidence tier</li>
              <li>Asks confirmation before spend guidance</li>
            </ul>
          </div>
        </div>
        <div className="chat-window">
          <div
            ref={chatMessagesRef}
            className="chat-messages"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
              >
                <div className="avatar">
                  {message.sender === 'user' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M12 7v10"></path>
                      <path d="M8.5 12H12"></path>
                    </svg>
                  )}
                </div>
                <div className="content">
                  {(() => {
                    if (message.sender === 'ai' && message.type === 'text') {
                      return (
                        <div className="markdown-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
                        </div>
                      );
                    }
                    if (message.type === 'connect_required') {
                      return (
                        <ConnectAccountCard
                          platform={message.platform}
                          message={message.text}
                        />
                      );
                    }
                    if (message.type === 'linkedin_preview') {
                      return (
                        <LinkedInPreviewCard
                          preview={message.preview}
                          onAction={(action) => {
                            const p = message.preview;
                            const caption = [p.headline, p.body, p.cta, (p.hashtags || []).join(' ')]
                              .filter(Boolean)
                              .join('\n\n');
                            const actionMap = {
                              approve: `SCHEDULE_APPROVED: ${JSON.stringify({
                                organization_id:  p.organization_id,
                                organization_name: p.org_name,
                                caption,
                                scheduled_at_iso: p.scheduled_at_utc,
                              })}`,
                              edit:        `Edit the post caption for ${p.org_name}. Current caption: ${caption}`,
                              change_org:  `Schedule this post for a DIFFERENT organization — do NOT use "${p.org_name}". Keep same content and time. Content: ${caption}. Time: ${p.scheduled_at_utc}.`,
                              change_time: `Reschedule the ${p.org_name} post to a different time. Keep the same content. Content: ${caption}`,
                            };
                            triggerLinkedInCampaign(actionMap[action] || action);
                          }}
                        />
                      );
                    }
                    if (message.type === 'linkedin_campaign') {
                      return (
                        <LinkedInCampaignCard
                          message={message}
                          onSchedule={triggerLinkedInCampaign}
                        />
                      );
                    }
                    return message.text;
                  })()}
                  {message.type === 'image' && message.imageUrl && (
                    <div className="generated-image-container">
                      {message.totalImages > 1 && (
                        <div className="image-counter">
                          Poster {message.imageIndex} of {message.totalImages}
                        </div>
                      )}
                      <img
                        src={message.imageUrl}
                        alt={message.imageIndex ? `Generated poster ${message.imageIndex}` : 'Generated poster'}
                        className="generated-image"
                      />
                      <a
                        href={message.imageUrl}
                        download={message.imageIndex ? `generated-poster-${message.imageIndex}.png` : 'generated-poster.png'}
                        className="download-btn"
                      >
                        📥 Download Poster{message.totalImages > 1 ? ` ${message.imageIndex}` : ''}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="message ai-message">
                <div className="avatar">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M12 7v10"></path>
                    <path d="M8.5 12H12"></path>
                  </svg>
                </div>
                <div className="content loading">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <form onSubmit={handleSendMessage} className="chat-input-form">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask about campaigns, content, or analytics. For images, use Gallery."
                className="chat-input"
                autoComplete="off"
                disabled={isLoading}
              />

              <button
                type="submit"
                className="icon-button send-button"
                title="Send"
                disabled={isLoading || !inputValue.trim()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatBot;
